import _ from "lodash";
import cn from "classnames";
import { faBolt, faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { Button, Col, Container, Row } from "reactstrap";
import {
  CARD_CHECK_LOADING,
  PLAN_COUPON_LOADING,
  PLAN_LOADING,
  UPGRADE_PLAN_LOADING,
} from "../../constants/loading.constant";
import { usePaymentCore } from "../../cores/payment";
import { useMeCore } from "../../cores/me";
import { usePlanCore } from "../../cores/plan";
import { PlanType } from "../../cores/plan/contants/planType.constant";
import { InputWembii } from "../component/InputWembii";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import { CouponInfo } from "../component/plan/CouponInfo";
import { Plan, Rules } from "../../cores/plan/interfaces/api/get-plans";
import { useGlobalCore } from "../../cores/globals";
import { useRouterCore } from "../../cores/router";
import AreYouSure from "../component/AreYouSure";
import { CHANGE_SUBSCRIPTION_MODAL } from "../../constants/modal.constant";
import { CL$Format, parseChileMoneyString } from "../../cores/utils";
import { useIntl } from "react-intl";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import "../styles/Plans.scss";

/** Códigos de moneda (API); la etiqueta visible va por i18n (p. ej. CLP → «Pesos chilenos»). */
const PLAN_CURRENCIES = [
  { id: "CLP", labelId: "app.plansPage.currencyCLP" },
  { id: "USD", labelId: "app.plansPage.currencyUSD" },
  { id: "BRL", labelId: "app.plansPage.currencyBRL" },
  { id: "PEN", labelId: "app.plansPage.currencyPEN" },
] as const;
type PlanCurrencyCode = (typeof PLAN_CURRENCIES)[number]["id"];

const inferCurrencyFromBrowser = (): PlanCurrencyCode | null => {
  if (typeof navigator === "undefined") return null;
  const locale = (navigator.language || "").toLowerCase();
  if (!locale) return null;
  if (locale.includes("-pe")) return "PEN";
  if (locale.includes("-br")) return "BRL";
  if (locale.includes("-cl")) return "CLP";
  return null;
};

/** Orden estable de reglas en tarjetas (Canva oculto en esta vista). */
const PLAN_RULE_KEY_ORDER: (keyof Rules)[] = [
  "teamSize",
  "companyAmount",
  "webFormsAmount",
  "websitePagesAmount",
  "reportsAmount",
  "allowScheduledPost",
  "allowExportContact",
  "allowWebChat",
  "allowWhatsappChat",
  "allowAnalytics",
  "allowFacebookAdvertisements",
  "allowMarket",
  "allowActivityBoard",
  "allowBusinessBoard",
  "allowAutomation",
  "allowDatabase",
];

const RULE_LABEL_IDS: Record<string, string> = {
  teamSize: "app.plan.rule.teamSize",
  companyAmount: "app.plan.rule.companyAmount",
  webFormsAmount: "app.plan.rule.webFormsAmount",
  websitePagesAmount: "app.plan.rule.websitePagesAmount",
  reportsAmount: "app.plan.rule.reportsAmount",
  allowScheduledPost: "app.plan.rule.allowScheduledPost",
  allowExportContact: "app.plan.rule.allowExportContact",
  allowWebChat: "app.plan.rule.allowWebChat",
  allowWhatsappChat: "app.plan.rule.allowWhatsappChat",
  allowAnalytics: "app.plan.rule.allowAnalytics",
  allowFacebookAdvertisements: "app.plan.rule.allowFacebookAdvertisements",
  allowMarket: "app.plan.rule.allowMarket",
  allowActivityBoard: "app.plan.rule.allowActivityBoard",
  allowBusinessBoard: "app.plan.rule.allowBusinessBoard",
  allowAutomation: "app.plan.rule.allowAutomation",
  allowDatabase: "app.plan.rule.allowDatabase",
};

const getPlanRuleKeysForDisplay = (plan: Plan): string[] => {
  const keys = new Set(
    Object.keys(plan.rules).filter((k) => k !== "allowCanva")
  );
  const ordered = PLAN_RULE_KEY_ORDER.filter((k) => keys.has(k));
  const rest = Array.from(keys)
    .filter(
      (k) => !(PLAN_RULE_KEY_ORDER as readonly string[]).includes(k)
    )
    .sort();
  return [...ordered, ...rest];
};

type PlanCardFeatureLine = { text: string; variant: "on" | "off" | "neutral" };

/** Todas las reglas del plan para la tarjeta (sin Canva). */
const getPlanFeatureLinesForCard = (
  plan: Plan,
  intl: { formatMessage: (d: { id: string }) => string }
): PlanCardFeatureLine[] => {
  const rules = plan.rules as unknown as Record<
    string,
    string | number | boolean
  >;
  const lines: PlanCardFeatureLine[] = [];
  for (const key of getPlanRuleKeysForDisplay(plan)) {
    if (key === "allowCanva") continue;
    const id = RULE_LABEL_IDS[key];
    if (!id) continue;
    const v = rules[key];
    if (v === undefined || v === null) continue;
    const label = intl.formatMessage({ id });
    if (typeof v === "boolean") {
      lines.push({
        text: `${label}: ${intl.formatMessage({
          id: v ? "app.plansPage.ruleYes" : "app.plansPage.ruleNo",
        })}`,
        variant: v ? "on" : "off",
      });
    } else {
      lines.push({ text: `${label}: ${v}`, variant: "neutral" });
    }
  }
  return lines;
};

/** Solicitar demo / contacto ventas (sitio público Wembii). */
const PLANS_SALES_DEMO_URL = "https://wembii.com/solicitar-demo";

/** Fallback si el API aún no envía `aiCreditsMonthly` (misma lógica que seed en lith). */
const DEFAULT_AI_CREDITS_BY_PLAN_NAME: Record<string, number> = {
  [PlanType.FREE]: 10,
  "app.plan.Smart": 100,
  "app.plan.Pro": 200,
  "app.plan.Premium": 500,
};

const getPlanAiCreditsMonthly = (plan: Plan): number => {
  const raw = plan.aiCreditsMonthly;
  const n = typeof raw === "number" ? raw : Number(raw);
  if (Number.isFinite(n) && n > 0) return Math.floor(n);
  const fb = DEFAULT_AI_CREDITS_BY_PLAN_NAME[plan.name];
  return fb != null ? fb : 0;
};

export const Plans = () => {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState(0);
  const [couponTxt, setCouponTxt] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [checkedCoupon, setCheckedCoupon] = useState(false);
  const [payWithBank, setPayWithBank] = useState(false);
  const [cardValid] = useState(false);
  const [coin, setCoin] = useState<PlanCurrencyCode>("USD");
  const [type, setType] = useState("monthly");
  const { getAllPlans, getPlans, plans } = usePlanCore();
  const { goToMeCompany } = useRouterCore();
  const { reset, turnOffModal, turnOnModal, isLoading } = useGlobalCore();
  const {
    postFlowSubscription,
    deleteSubscription,
    getFlowCoupon,
    getStripeCoupon,
    postStripeSubscription,
    resetCoupon,
    coupon,
  } = usePaymentCore();
  const {
    userPlan,
    companyPlan,
    companyId,
    companyRole,
    deselectCompany,
    isAdmin,
    isReseller,
  } = useMeCore();

  /** Plan de facturación de la compañía seleccionada (owner); si no hay, el del usuario. */
  const billingPlan = useMemo(() => {
    if (companyId != null && companyPlan != null) return companyPlan;
    return userPlan ?? null;
  }, [companyId, companyPlan, userPlan]);

  /** Catálogo sin FREE. Dedupe por `name` y prioriza IDs más nuevos (evita mostrar tiers legacy). */
  const displayPlans = useMemo(() => {
    const paid = plans.filter((p) => p.name !== PlanType.FREE);
    const tierKey = (p: Plan) =>
      p.name && String(p.name).trim() !== ""
        ? String(p.name)
        : `__id:${p.id}`;

    const byKey = new Map<string, Plan>();
    for (const p of paid) {
      const key = tierKey(p);
      const prev = byKey.get(key);
      if (!prev) {
        byKey.set(key, p);
      } else if (p.id > prev.id) {
        byKey.set(key, p);
      }
    }
    const seen = new Set<number>();
    const ordered: Plan[] = [];
    for (const p of paid) {
      const canonical = byKey.get(tierKey(p))!;
      if (!seen.has(canonical.id)) {
        seen.add(canonical.id);
        ordered.push(canonical);
      }
    }
    return ordered;
  }, [plans]);

  const formatPlanPriceParts = useCallback(
    (
      plan: Plan | null
    ): { amount: string; code: string; periodLine: string } | null => {
      if (!plan) return null;
      const periodBase =
        type === "annual"
          ? intl.formatMessage({ id: "app.plansPage.perYear" })
          : intl.formatMessage({ id: "app.plansPage.perMonth" });
      const periodLine =
        coin === "CLP"
          ? `${periodBase} · ${intl.formatMessage({ id: "app.plan.iva" })}`
          : periodBase;

      let priceVal: string;
      switch (coin) {
        case "CLP":
          if (payWithBank && cardValid) {
            priceVal =
              type === "annual"
                ? plan.annualFlowChileBankPrice!
                : plan.flowChileBankPrice!;
          } else {
            priceVal =
              type === "annual" ? plan.annualFlowPrice! : plan.flowPrice!;
          }
          {
            const amount = parseChileMoneyString(priceVal);
            if (!Number.isFinite(amount)) return null;
            const prefix = intl.formatMessage({
              id: "app.plansPage.planPricePrefixCLP",
            });
            return {
              amount: CL$Format(amount, "", prefix),
              code: intl.formatMessage({ id: "app.plansPage.currencyCodeCLP" }),
              periodLine,
            };
          }
        case "USD":
          priceVal =
            type === "annual"
              ? plan.annualStripeUsdPrice!
              : plan.stripeUsdPrice!;
          {
            const n = parseFloat(String(priceVal).replace(/,/g, ""));
            if (!Number.isFinite(n)) return null;
            return {
              amount: n.toLocaleString(intl.locale || "en", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }),
              code: intl.formatMessage({ id: "app.plansPage.currencyCodeUSD" }),
              periodLine,
            };
          }
        case "BRL":
          priceVal =
            type === "annual"
              ? plan.annualStripeBrlPrice!
              : plan.stripeBrlPrice!;
          {
            const n = parseFloat(String(priceVal).replace(/,/g, ""));
            if (!Number.isFinite(n)) return null;
            return {
              amount: n.toLocaleString(intl.locale || "en", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }),
              code: intl.formatMessage({ id: "app.plansPage.currencyCodeBRL" }),
              periodLine,
            };
          }
        case "PEN":
          priceVal =
            type === "annual"
              ? plan.annualStripePenPrice!
              : plan.stripePenPrice!;
          {
            const n = parseFloat(String(priceVal).replace(/,/g, ""));
            if (!Number.isFinite(n)) return null;
            return {
              amount: n.toLocaleString(intl.locale || "en", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }),
              code: intl.formatMessage({ id: "app.plansPage.currencyCodePEN" }),
              periodLine,
            };
          }
        default:
          return null;
      }
    },
    [coin, type, payWithBank, cardValid, intl]
  );

  const formatPlanPrice = useCallback(
    (plan: Plan | null) => {
      const p = formatPlanPriceParts(plan);
      if (!p) return "—";
      if (coin === "CLP") return p.amount;
      return `${p.code} ${p.amount}`;
    },
    [formatPlanPriceParts, coin]
  );

  const price = useMemo(
    () => formatPlanPrice(selectedPlan),
    [formatPlanPrice, selectedPlan]
  );

  const selectedPriceParts = useMemo(
    () => formatPlanPriceParts(selectedPlan),
    [formatPlanPriceParts, selectedPlan]
  );

  const checkoutContextLine = useMemo(() => {
    const billing = intl.formatMessage({
      id: type === "annual" ? "app.plan.annual" : "app.plan.monthly",
    });
    const currencyLabel = intl.formatMessage({
      id:
        PLAN_CURRENCIES.find((c) => c.id === coin)?.labelId ??
        "app.plansPage.currencyCLP",
    });
    return intl.formatMessage(
      { id: "app.plansPage.checkoutContext" },
      { billing, currency: currencyLabel }
    );
  }, [intl, type, coin]);

  const featuredPlanId = useMemo(() => {
    if (displayPlans.length < 3) return null;
    return displayPlans[Math.floor(displayPlans.length / 2)]?.id ?? null;
  }, [displayPlans]);

  /** Fila completa del plan de facturación (precios según moneda/ciclo del toolbar). */
  const billingPlanFull = useMemo<Plan | null>(() => {
    if (!billingPlan) return null;
    return (
      plans.find((p) => p.id === billingPlan.id) ??
      (billingPlan as unknown as Plan)
    );
  }, [billingPlan, plans]);

  const showCancelInStrip = useMemo(
    () =>
      (!companyRole || companyRole.owner) &&
      !!billingPlanFull &&
      billingPlanFull.name !== PlanType.FREE,
    [companyRole, billingPlanFull]
  );

  const currentBarBillingLine = useMemo(() => {
    const billing = intl.formatMessage({
      id: type === "annual" ? "app.plan.annual" : "app.plan.monthly",
    });
    const currencyLabel = intl.formatMessage({
      id:
        PLAN_CURRENCIES.find((c) => c.id === coin)?.labelId ??
        "app.plansPage.currencyCLP",
    });
    return intl.formatMessage(
      { id: "app.plansPage.currentBarBilling" },
      { billing, currency: currencyLabel }
    );
  }, [intl, type, coin]);

  const checkoutSectionRef = useRef<HTMLDivElement>(null);
  const userChangedCoinRef = useRef(false);
  const autoCoinResolvedRef = useRef(false);

  const scrollToCheckout = useCallback(() => {
    window.requestAnimationFrame(() =>
      checkoutSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    );
  }, []);

  const selectPlan = useCallback(
    (planId: number) => {
      setActiveTab(planId);
      setSelectedPlan(plans.find(({ id }) => id === planId) ?? null);
    },
    [plans]
  );

  const handleCardSurfaceClick = useCallback(
    (e: React.MouseEvent, planId: number) => {
      if ((e.target as HTMLElement).closest("button, a")) return;
      selectPlan(planId);
    },
    [selectPlan]
  );

  useEffect(() => {
    // plans/all solo admin/reseller; el resto usa GET plans/ (evita 403 y estado vacío).
    if (isAdmin || isReseller) {
      getAllPlans();
    } else {
      getPlans();
    }
    resetCoupon();
  }, [
    getAllPlans,
    getPlans,
    resetCoupon,
    billingPlan,
    isAdmin,
    isReseller,
  ]);

  const handleCardKeyDown = useCallback(
    (e: React.KeyboardEvent, planId: number) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        selectPlan(planId);
      }
    },
    [selectPlan]
  );

  const handleClickUpgrade = useCallback(async () => {
    const couponId = payWithBank ? null : coupon?.id || null;
    let urlData;
    switch (coin) {
      case "CLP":
        urlData = await postFlowSubscription(
          payWithBank && cardValid
            ? type === "annual"
              ? selectedPlan?.annualFlowChileBankId!
              : selectedPlan?.flowChileBankId!
            : type === "annual"
              ? selectedPlan?.annualFlowId!
              : selectedPlan?.flowId!,
          couponId
        );
        break;
      case "USD":
        urlData = await postStripeSubscription(
          type === "annual"
            ? selectedPlan?.annualStripeUsdId!
            : selectedPlan?.stripeUsdId!,
          couponId
        );
        break;
      case "BRL":
        urlData = await postStripeSubscription(
          type === "annual"
            ? selectedPlan?.annualStripeBrlId!
            : selectedPlan?.stripeBrlId!,
          couponId
        );
        break;
      case "PEN":
        urlData = await postStripeSubscription(
          type === "annual"
            ? selectedPlan?.annualStripePenId!
            : selectedPlan?.stripePenId!,
          couponId
        );
        break;
    }

    if (urlData) window.open(urlData.url, "_self");
  }, [
    postFlowSubscription,
    postStripeSubscription,
    coupon,
    type,
    selectedPlan,
    coin,
    cardValid,
    payWithBank,
  ]);

  const handleClickCancel = useCallback(() => {
    turnOnModal(
      CHANGE_SUBSCRIPTION_MODAL,
      <AreYouSure
        message={`Está seguro de querer cancelar la subscripción activa? Se pasará a tener el Plan FREE`}
        onSubmit={() => {
          deleteSubscription(() => {
            reset();
            deselectCompany();
            goToMeCompany();
          });
          turnOffModal(CHANGE_SUBSCRIPTION_MODAL);
        }}
        onClose={() => turnOffModal(CHANGE_SUBSCRIPTION_MODAL)}
      />
    );
  }, [
    turnOnModal,
    turnOffModal,
    deleteSubscription,
    reset,
    goToMeCompany,
    deselectCompany,
  ]);

  const debouncedCheckCoupon = useRef(
    _.debounce(
      (couponIn, coinIn) =>
        couponIn &&
        (coinIn === "CLP"
          ? getFlowCoupon(couponIn, () => {
              setCheckedCoupon(true);
            })
          : getStripeCoupon(couponIn, () => {
              setCheckedCoupon(true);
            })),
      1000
    )
  ).current;

  useEffect(() => {
    setCheckedCoupon(false);
    debouncedCheckCoupon(couponTxt, coin);
  }, [debouncedCheckCoupon, couponTxt, coin]);

  useEffect(() => {
    if (!billingPlan) return;
    const match = plans.find(({ id }) => id === billingPlan.id) || null;
    if (match?.name === PlanType.FREE) {
      const firstPaid = displayPlans[0];
      setActiveTab(firstPaid?.id ?? 0);
      setSelectedPlan(firstPaid ?? null);
    } else {
      setActiveTab(billingPlan.id);
      setSelectedPlan(match);
    }
  }, [billingPlan, plans, displayPlans, setSelectedPlan]);

  /** Moneda inicial: idioma del navegador (es-CL / es-PE / pt-BR) o USD. */
  useEffect(() => {
    if (autoCoinResolvedRef.current || userChangedCoinRef.current) return;
    const next: PlanCurrencyCode = inferCurrencyFromBrowser() ?? "USD";
    autoCoinResolvedRef.current = true;
    setCoin(next);
  }, []);

  /** Al cargar la grilla: leve scroll en la lista de features del plan central (sugiere más ítems abajo). */
  const featureChecklistScrollDemoDoneRef = useRef(false);
  useEffect(() => {
    if (isLoading(PLAN_LOADING) || displayPlans.length === 0) return;
    if (featureChecklistScrollDemoDoneRef.current) return;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      const nodes = document.querySelectorAll<HTMLElement>(
        ".plans-page .plans-card__checklist-scroll"
      );
      const wrap =
        (nodes.length > 1 ? nodes[Math.floor(nodes.length / 2)] : null) ??
        nodes.item(0);
      if (!wrap) {
        featureChecklistScrollDemoDoneRef.current = true;
        return;
      }

      const maxScroll = wrap.scrollHeight - wrap.clientHeight;
      featureChecklistScrollDemoDoneRef.current = true;
      if (maxScroll < 16) return;

      const reduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduceMotion) return;

      const startTop = wrap.scrollTop;
      const target = Math.min(64, maxScroll);
      const downMs = 550;
      let startTs = 0;

      const ease = (t: number) => 0.5 - 0.5 * Math.cos(Math.PI * t);

      const tickDown = (ts: number) => {
        if (cancelled) return;
        if (!startTs) startTs = ts;
        const p = Math.min(1, (ts - startTs) / downMs);
        wrap.scrollTop = startTop + (target - startTop) * ease(p);
        if (p < 1) requestAnimationFrame(tickDown);
        else {
          window.setTimeout(() => {
            if (cancelled) return;
            const from = wrap.scrollTop;
            const backMs = 480;
            let t0 = 0;
            const tickBack = (ts2: number) => {
              if (cancelled) return;
              if (!t0) t0 = ts2;
              const p2 = Math.min(1, (ts2 - t0) / backMs);
              wrap.scrollTop = from * (1 - ease(p2));
              if (p2 < 1) requestAnimationFrame(tickBack);
            };
            requestAnimationFrame(tickBack);
          }, 420);
        }
      };

      requestAnimationFrame(tickDown);
    }, 550);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isLoading, displayPlans.length]);

  const showUpgradeOptions = useMemo(
    () =>
      (!companyRole || companyRole.owner) &&
      selectedPlan?.name !== PlanType.FREE &&
      billingPlan?.id !== selectedPlan?.id,
    [selectedPlan, billingPlan, companyRole]
  );

  const handleChangeType = useCallback(
    (next: string) => {
      resetCoupon();
      setPayWithBank(false);
      setType(next);
    },
    [resetCoupon]
  );

  const handleChangeCoin = useCallback(
    (next: string) => {
      userChangedCoinRef.current = true;
      resetCoupon();
      setPayWithBank(false);
      setCoin(next as PlanCurrencyCode);
    },
    [resetCoupon]
  );

  return (
    <Layout>
      <Content>
        <div className="plans-page">
          <Container fluid className="plans-page__inner position-relative">
            <UpdateLoadingCard
              className="position-absolute-center"
              loadingId={PLAN_LOADING}
              text={intl.formatMessage({ id: "app.plansPage.loadingPlans" })}
            />

            {!isLoading(PLAN_LOADING) && (
              <>
                <header className="plans-page__hero">
                  <h1 className="plans-page__title plans-page__title--hero-mock">
                    <span className="plans-page__title-before">
                      {intl.formatMessage({
                        id: "app.plansPage.heroTitleBefore",
                      })}
                    </span>
                    <span className="plans-page__title-ia">
                      {intl.formatMessage({ id: "app.plansPage.heroTitleIA" })}
                    </span>
                  </h1>
                  <p className="plans-page__subtitle">
                    {intl.formatMessage({ id: "app.plansPage.heroSubtitle" })}
                  </p>

                  <div className="plans-page__dock">
                    <div className="plans-page__dock-main">
                      <div className="plans-page__dock-label">
                        {intl.formatMessage({ id: "app.plansPage.dockBilling" })}
                      </div>
                      <div
                        className="plans-billing-toggle"
                        role="group"
                        aria-label={intl.formatMessage({
                          id: "app.plansPage.billing",
                        })}
                      >
                        <button
                          type="button"
                          className={cn("plans-billing-toggle__btn", {
                            "plans-billing-toggle__btn--active":
                              type === "monthly",
                          })}
                          onClick={() => handleChangeType("monthly")}
                          aria-pressed={type === "monthly"}
                        >
                          {intl.formatMessage({ id: "app.plan.monthly" })}
                        </button>
                        <button
                          type="button"
                          className={cn("plans-billing-toggle__btn", {
                            "plans-billing-toggle__btn--active":
                              type === "annual",
                          })}
                          onClick={() => handleChangeType("annual")}
                          aria-pressed={type === "annual"}
                        >
                          {intl.formatMessage({ id: "app.plan.annual" })}
                        </button>
                      </div>
                    </div>

                    <div className="plans-page__dock-divider" aria-hidden />

                    <div className="plans-page__dock-main plans-page__dock-main--currency">
                      <div className="plans-page__dock-label">
                        {intl.formatMessage({ id: "app.plansPage.currency" })}
                      </div>
                      <div
                        className="plans-currency"
                        role="group"
                        aria-label={intl.formatMessage({
                          id: "app.plansPage.currency",
                        })}
                      >
                        {PLAN_CURRENCIES.map(({ id, labelId }) => (
                          <button
                            key={id}
                            type="button"
                            className={cn("plans-currency__chip", {
                              "plans-currency__chip--active": coin === id,
                            })}
                            onClick={() => handleChangeCoin(id)}
                            aria-pressed={coin === id}
                          >
                            {intl.formatMessage({ id: labelId })}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </header>

                <div className="plans-grid">
                  {displayPlans.map((plan, planIndex) => {
                    const isSelected = activeTab === plan.id;
                    const isCurrent = billingPlan?.id === plan.id;
                    const isFeatured =
                      featuredPlanId !== null && plan.id === featuredPlanId;
                    const priceParts = formatPlanPriceParts(plan);
                    const aiCredits = getPlanAiCreditsMonthly(plan);
                    const planAccent = plan.color || "#0b65c2";
                    const featureLines = getPlanFeatureLinesForCard(plan, intl);
                    const aiHeadingId =
                      planIndex === 0
                        ? "app.plansPage.aiBoxHeadingTier1"
                        : planIndex === displayPlans.length - 1
                          ? "app.plansPage.aiBoxHeadingTier3"
                          : "app.plansPage.aiBoxHeadingTier2";
                    const aiFmt = new Intl.NumberFormat(
                      intl.locale || undefined
                    ).format(aiCredits);

                    return (
                      <article
                        key={plan.id}
                        tabIndex={0}
                        className={cn("plans-card", {
                          "plans-card--selected": isSelected,
                          "plans-card--featured": isFeatured,
                          "plans-card--current": isCurrent,
                        })}
                        style={
                          {
                            "--plan-accent": planAccent,
                          } as React.CSSProperties
                        }
                        onClick={(e) => handleCardSurfaceClick(e, plan.id)}
                        onKeyDown={(e) => handleCardKeyDown(e, plan.id)}
                        aria-current={isSelected ? "true" : undefined}
                      >
                        <div className="plans-card__accent-bar" aria-hidden />
                        {isFeatured && (
                          <div className="plans-card__popular-pill">
                            {intl.formatMessage({
                              id: "app.plansPage.featured",
                            })}
                          </div>
                        )}
                        <div className="plans-card__inner">
                          <div className="plans-card__head">
                            <h3 className="plans-card__name">
                              {intl.formatMessage({ id: plan.name })}
                            </h3>
                            {isCurrent && (
                              <span className="plans-card__current-pill">
                                {intl.formatMessage({
                                  id: "app.plansPage.yourPlan",
                                })}
                              </span>
                            )}
                          </div>

                          <div className="plans-card__price-row">
                            {priceParts ? (
                              <>
                                <span className="plans-card__price-main">
                                  {priceParts.amount}
                                </span>
                                <span className="plans-card__price-rest">
                                  {coin !== "CLP" && (
                                    <span className="plans-card__currency-inline">
                                      {priceParts.code}
                                    </span>
                                  )}
                                  <span className="plans-card__price-slash">
                                    {type === "annual"
                                      ? intl.formatMessage({
                                          id: "app.plansPage.perYearShort",
                                        })
                                      : intl.formatMessage({
                                          id: "app.plansPage.perMonthShort",
                                        })}
                                  </span>
                                </span>
                              </>
                            ) : (
                              <span className="plans-card__price-main">—</span>
                            )}
                          </div>

                          <div className="plans-card__ai-box">
                            <div className="plans-card__ai-box-head">
                              {intl.formatMessage({ id: aiHeadingId })}
                            </div>
                            <div className="plans-card__ai-box-amount">
                              {aiCredits > 0 ? aiFmt : "—"}
                            </div>
                            <div className="plans-card__ai-box-caption">
                              {intl.formatMessage({
                                id: "app.plansPage.aiBoxCaption",
                              })}
                            </div>
                          </div>

                          <div
                            className="plans-card__checklist-scroll"
                            role="region"
                            aria-label={intl.formatMessage({
                              id: "app.plansPage.featuresListAria",
                            })}
                          >
                            <ul className="plans-card__checklist">
                              {featureLines.map((line, li) => (
                                <li
                                  key={`${plan.id}-${li}`}
                                  className="plans-card__checklist-item"
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      line.variant === "off"
                                        ? faTimes
                                        : faCheck
                                    }
                                    className={cn("plans-card__checklist-icon", {
                                      "plans-card__checklist-icon--off":
                                        line.variant === "off",
                                    })}
                                    aria-hidden
                                  />
                                  <span>{line.text}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <button
                            type="button"
                            className={cn("plans-card__cta", {
                              "plans-card__cta--primary":
                                isFeatured && !isCurrent,
                              "plans-card__cta--current": isCurrent,
                            })}
                            disabled={isCurrent}
                            onClick={(e) => {
                              e.stopPropagation();
                              selectPlan(plan.id);
                              if (!isCurrent) scrollToCheckout();
                            }}
                          >
                            {isCurrent
                              ? intl.formatMessage({
                                  id: "app.plansPage.cardCtaCurrentPlan",
                                })
                              : intl.formatMessage({
                                  id: "app.plansPage.subscribeCta",
                                })}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>

                {billingPlanFull && (
                  <div className="plans-page__current-bar" role="status">
                    <div className="plans-page__current-bar-icon" aria-hidden>
                      <FontAwesomeIcon icon={faCheck} />
                    </div>
                    <div className="plans-page__current-bar-body">
                      <div className="plans-page__current-bar-title">
                        <span>
                          {intl.formatMessage({
                            id: "app.plansPage.currentBarTitleBefore",
                          })}
                        </span>{" "}
                        <span className="plans-page__current-bar-plan-name">
                          {intl.formatMessage({ id: billingPlanFull.name })}
                        </span>
                      </div>
                    </div>
                    {showCancelInStrip && (
                      <button
                        type="button"
                        className="plans-page__current-bar-cancel"
                        onClick={handleClickCancel}
                      >
                        {intl.formatMessage({
                          id: "app.plansPage.cancelSubscriptionLink",
                        })}
                      </button>
                    )}
                  </div>
                )}

                <UpdateLoadingCard
                  loadingId={UPGRADE_PLAN_LOADING}
                  text={intl.formatMessage({ id: "app.plansPage.changingPlan" })}
                />
                <UpdateLoadingCard
                  loadingId={PLAN_COUPON_LOADING}
                  text={intl.formatMessage({ id: "app.plansPage.searchingCoupon" })}
                />
                <UpdateLoadingCard
                  loadingId={CARD_CHECK_LOADING}
                  text={intl.formatMessage({ id: "app.plansPage.checkingCard" })}
                />

                {showUpgradeOptions && selectedPlan && (
                  <div
                    ref={checkoutSectionRef}
                    className="plans-checkout plans-checkout--panel"
                    aria-labelledby="plans-checkout-heading"
                  >
                    <div className="plans-checkout__top">
                      <span className="plans-checkout__eyebrow">
                        {intl.formatMessage({
                          id: "app.plansPage.checkoutEyebrowUpgrade",
                        })}
                      </span>
                      <h2
                        id="plans-checkout-heading"
                        className="plans-checkout__title"
                      >
                        {intl.formatMessage(
                          {
                            id: "app.plansPage.checkoutTitleUpgrade",
                          },
                          {
                            plan: intl.formatMessage({
                              id: selectedPlan.name,
                            }),
                          }
                        )}
                      </h2>
                      <p className="plans-checkout__context">
                        {checkoutContextLine}
                      </p>
                    </div>

                    <div className="plans-checkout__price-block">
                      <div className="plans-checkout__price-label">
                        {intl.formatMessage({
                          id: "app.plansPage.checkoutPriceLabelUpgrade",
                        })}
                      </div>
                      {selectedPriceParts ? (
                        <div className="plans-checkout__price-row">
                          <div className="plans-checkout__price-value">
                            {selectedPriceParts.amount}
                          </div>
                          <div className="plans-checkout__price-aside">
                            <span className="plans-checkout__currency-pill">
                              {selectedPriceParts.code}
                            </span>
                            <span className="plans-checkout__price-period">
                              {selectedPriceParts.periodLine}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="plans-checkout__price-value">
                          {price}
                        </div>
                      )}
                      {selectedPlan &&
                        getPlanAiCreditsMonthly(selectedPlan) > 0 && (
                          <div className="plans-checkout__ai-line">
                            <FontAwesomeIcon
                              icon={faBolt}
                              className="plans-checkout__ai-line-icon"
                              aria-hidden
                            />
                            <span>
                              {intl.formatMessage(
                                { id: "app.plansPage.checkoutAiCredits" },
                                {
                                  amount: getPlanAiCreditsMonthly(
                                    selectedPlan
                                  ),
                                }
                              )}
                            </span>
                          </div>
                        )}
                      <p className="plans-checkout__help">
                        {intl.formatMessage({
                          id: "app.plansPage.checkoutHelpUpgrade",
                        })}
                      </p>
                    </div>

                    <div className="plans-checkout__actions">
                      {!payWithBank && (
                        <Row className="w-100 mb-2">
                          <Col xs="12" md="8">
                            <InputWembii
                              label={intl.formatMessage({
                                id: "app.plansPage.couponLabel",
                              })}
                              value={couponTxt}
                              mb={false}
                              onChange={setCouponTxt}
                            />
                          </Col>
                          <Col
                            xs="12"
                            md="4"
                            className="d-flex align-items-end justify-content-md-end pt-2 pt-md-0"
                          >
                            {checkedCoupon && <CouponInfo coupon={coupon} />}
                          </Col>
                        </Row>
                      )}
                      <Button
                        type="button"
                        className="plans-btn-primary"
                        onClick={handleClickUpgrade}
                      >
                        {intl.formatMessage({
                          id: "app.plansPage.subscribeCta",
                        })}
                      </Button>
                    </div>
                  </div>
                )}

                <footer className="plans-page__enterprise">
                  <div className="plans-page__enterprise-copy">
                    <h2 className="plans-page__enterprise-title">
                      {intl.formatMessage({
                        id: "app.plansPage.enterpriseTitle",
                      })}
                    </h2>
                    <p className="plans-page__enterprise-body">
                      {intl.formatMessage({
                        id: "app.plansPage.enterpriseBody",
                      })}
                    </p>
                  </div>
                  <a
                    className="plans-page__enterprise-cta"
                    href={PLANS_SALES_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {intl.formatMessage({
                      id: "app.plansPage.enterpriseCta",
                    })}
                  </a>
                </footer>
              </>
            )}
          </Container>
        </div>
      </Content>
    </Layout>
  );
};
